import type { DefaultContext, OperationVariables } from "@apollo/client";
import {
  skipToken,
  useQuery as useApolloQuery,
  type SkipToken,
} from "@apollo/client/react";
import {
  type ForbiddenAwareOnError,
  withWrappedForbiddenOnError,
} from "./wrapForbiddenAwareOnError";

type ExtendQueryOptions<TData, TVariables extends OperationVariables> = Exclude<
  Parameters<typeof useApolloQuery<TData, TVariables>>[1],
  SkipToken | undefined
> &
  Record<string, unknown> & {
    context?:
      | DefaultContext
      | ((hookContext: DefaultContext | undefined) => DefaultContext);
    onError?: ForbiddenAwareOnError;
  };

export function useQuery<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: Parameters<typeof useApolloQuery<TData, TVariables>>[0],
  ...args: {} extends TVariables
    ? [options?: SkipToken | ExtendQueryOptions<TData, TVariables> | undefined]
    : [options: SkipToken | ExtendQueryOptions<TData, TVariables>]
) {
  const options = args[0];

  let resolvedForWrap:
    SkipToken | ExtendQueryOptions<TData, TVariables> | Record<string, never>;
  if (options === skipToken) {
    resolvedForWrap = skipToken;
  } else if (options === undefined) {
    resolvedForWrap = {};
  } else {
    resolvedForWrap = options;
  }

  let contextForSkipFlag: DefaultContext | undefined;
  if (resolvedForWrap === skipToken) {
    contextForSkipFlag = undefined;
  } else if (typeof resolvedForWrap.context === "function") {
    contextForSkipFlag = undefined;
  } else {
    contextForSkipFlag = resolvedForWrap.context;
  }

  const mergedOptions =
    resolvedForWrap === skipToken
      ? skipToken
      : withWrappedForbiddenOnError(resolvedForWrap, contextForSkipFlag);

  return useApolloQuery(query, mergedOptions as never);
}
